import { PARTICIPANT_PHOTO_URL_SCHEME } from "../schemas/storage-file.schemas.js";
import {
  ParticipantPhotoUrlSchema,
  toParticipantPhotoUrlOrNull,
  type ParticipantPhotoUrl,
} from "../schemas/participant-photo-url.schemas.js";

interface WithPhotoUrl {
  readonly photoUrl: string | null;
}

interface WithRequesterAndTargetPhotoUrls {
  readonly requesterPhotoUrl: string | null;
  readonly targetPhotoUrl: string | null;
}

/**
 * Resolves `photoUrl` on every row: the API ships the raw stored value and only
 * the client knows the event it resolves against.
 */
export function resolvePhotoUrls<T extends WithPhotoUrl>(
  rows: readonly T[],
  eventIdPublic: string,
): T[] {
  return rows.map((row) => ({
    ...row,
    photoUrl: resolvePhotoUrl(row.photoUrl, eventIdPublic),
  }));
}

/**
 * Resolves both `photoUrl` fields a connection-request row carries. Each side
 * resolves its own stored value; crossing them would render the wrong person's
 * face beside a name.
 */
export function resolveRequesterAndTargetPhotoUrls<
  T extends WithRequesterAndTargetPhotoUrls,
>(rows: readonly T[], eventIdPublic: string): T[] {
  return rows.map((row) => ({
    ...row,
    requesterPhotoUrl: resolvePhotoUrl(row.requesterPhotoUrl, eventIdPublic),
    targetPhotoUrl: resolvePhotoUrl(row.targetPhotoUrl, eventIdPublic),
  }));
}

export function resolvePhotoUrl(
  url: string | null | undefined,
  eventIdPublic: string,
): ParticipantPhotoUrl | null {
  if (url == null) {
    return null;
  }
  if (!url.startsWith(PARTICIPANT_PHOTO_URL_SCHEME)) {
    return toParticipantPhotoUrlOrNull(url);
  }
  const path = url.slice(PARTICIPANT_PHOTO_URL_SCHEME.length);
  const segments = path.split("/").filter(Boolean);

  const participantId = segments[0];
  const photoKey = segments[1];
  if (
    segments.length === 2 &&
    participantId !== undefined &&
    photoKey !== undefined
  ) {
    return ParticipantPhotoUrlSchema.parse(
      `/api/v1/events/${encodeURIComponent(eventIdPublic)}/participants/${encodeURIComponent(participantId)}/photo?v=${encodeURIComponent(photoKey)}`,
    );
  }

  // Malformed scheme URL (not exactly `<participantId>/<photoKey>`): it cannot be
  // resolved to a fetchable path, and returning the raw `evt-participant-photo://`
  // value would render as a broken <img>. Degrade to no-photo so the fallback
  // avatar shows instead.
  return null;
}
